import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  useMyCompany,
  useEnrichMyCompany,
  useUpdateMyCompany,
} from "@/hooks/useCompanies";
import { ContactForm, ContactList } from "@/components/companies/contact-panel";
import { CompanyLogo } from "@/components/companies/company-logo";
import { EnableScanPanel } from "@/components/companies/enable-scan-panel";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import { ApiError } from "@/api/client";

export function CompaniesDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: merged, isLoading, error } = useMyCompany(slug);
  const enrichCompany = useEnrichMyCompany();
  const updateCompany = useUpdateMyCompany(slug ?? "");
  const [showContactForm, setShowContactForm] = useState(false);
  const [notes, setNotes] = useState("");

  const company = merged;
  const global = company?.global;
  const user = company?.user;
  const display = company?.display;
  const displayName = company?.name ?? slug;
  const website =
    user?.url ??
    display?.website ??
    global?.url ??
    global?.website ??
    (display?.domain || global?.domain
      ? `https://${display?.domain ?? global?.domain}`
      : undefined);
  const careersUrl =
    user?.careersUrl ?? global?.careersUrl ?? global?.careersPage;
  const research = user?.research ?? global?.research;
  const description = display?.description ?? global?.description;
  const industry = display?.industry ?? global?.industry;
  const headquarters =
    display?.headquarters ?? global?.headquarters ?? global?.locations?.[0];
  const size = display?.size ?? global?.size;
  const foundedYear = display?.foundedYear ?? global?.foundedYear;
  const hqCountry = display?.hqCountry ?? global?.hqCountry;
  const logoUrl = display?.logoUrl ?? global?.logoUrl;
  const domain = display?.domain ?? global?.domain;
  const linkedin =
    display?.socialMedia?.linkedin ?? global?.socialMedia?.linkedin;
  const enrichment = user?.enrichment;
  const companyJobs = company?.jobs ?? [];
  const jobsPagination = useClientPagination(companyJobs);

  const enrichError =
    enrichCompany.error instanceof ApiError
      ? enrichCompany.error.message
      : enrichCompany.error
        ? String(enrichCompany.error)
        : null;
  const serperMissing =
    Boolean(enrichError) &&
    (/SERPER|Settings → AI|Settings -> AI/i.test(enrichError ?? "") ||
      (enrichCompany.error instanceof ApiError &&
        (enrichCompany.error.body as { code?: string } | undefined)?.code ===
          "SERPER_NOT_CONFIGURED"));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="mx-auto max-w-3xl">
        <Link to="/companies" className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline">
          Back to Companies
        </Link>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">Company not found.</p>
        </div>
      </div>
    );
  }

  async function handleSaveNotes() {
    if (!slug) return;
    await updateCompany.mutateAsync({ notes });
  }

  async function handleEnrich() {
    if (!slug) return;
    try {
      await enrichCompany.mutateAsync(slug);
    } catch {
      // surfaced via enrichCompany.error
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/companies" className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline">
        Back to Companies
      </Link>

      <div className="mb-6 rounded-lg border border-border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-4">
            <CompanyLogo
              name={displayName ?? slug ?? ""}
              logoUrl={logoUrl}
              domain={domain}
              size="lg"
            />
            <div>
              <h1 className="text-2xl font-bold text-foreground">{displayName}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {industry && (
                  <span className="text-sm text-muted-foreground">{industry}</span>
                )}
                {company.isSeeded && (
                  <span className="rounded-full bg-sky-500/10 px-2 py-0.5 text-[10px] text-sky-400">
                    Seeded directory
                  </span>
                )}
                {(company.jobCount ?? 0) > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {company.jobCount} job{(company.jobCount ?? 0) !== 1 ? "s" : ""}
                  </span>
                )}
                {enrichment?.confidence && (
                  <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[10px] text-brand">
                    Enrichment {enrichment.confidence}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/explore?slug=${encodeURIComponent(company.slug)}&mode=company`}
              className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground transition hover:opacity-90"
            >
              Scan jobs
            </Link>
            <Link
              to={`/explore?slug=${encodeURIComponent(company.slug)}&mode=ai`}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-surface-hover"
            >
              Search web
            </Link>
            <Link
              to={`/ai-assistant?companySlug=${encodeURIComponent(company.slug)}`}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-surface-hover"
            >
              Ask AI
            </Link>
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-border px-3 py-1.5 text-xs text-brand hover:underline"
              >
                Website
              </a>
            )}
            {careersUrl && (
              <a
                href={careersUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-border px-3 py-1.5 text-xs text-brand hover:underline"
              >
                Careers
              </a>
            )}
            {linkedin && (
              <a
                href={linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-border px-3 py-1.5 text-xs text-brand hover:underline"
              >
                LinkedIn
              </a>
            )}
            <button
              type="button"
              onClick={handleEnrich}
              disabled={enrichCompany.isPending}
              className="rounded-md bg-brand/10 px-3 py-1.5 text-xs font-medium text-brand hover:bg-brand/20 disabled:opacity-50"
            >
              {enrichCompany.isPending ? "Enriching…" : "Enrich company"}
            </button>
          </div>
        </div>
      </div>

      {slug && (
        <EnableScanPanel slug={slug} company={company} />
      )}

      {enrichError && (
        <div className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
          <p>{enrichError}</p>
          {serperMissing && (
            <p className="mt-2 text-xs">
              Configure Serper in{" "}
              <Link to="/settings/ai" className="font-medium text-brand hover:underline">
                Settings → AI
              </Link>
              .
            </p>
          )}
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        {headquarters && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">HQ</p>
            <p className="text-sm">{headquarters}</p>
          </div>
        )}
        {size && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Size</p>
            <p className="text-sm">{size}</p>
          </div>
        )}
        {foundedYear && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Founded</p>
            <p className="text-sm">{foundedYear}</p>
          </div>
        )}
        {hqCountry && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Country</p>
            <p className="text-sm">{hqCountry}</p>
          </div>
        )}
        {industry && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Industry</p>
            <p className="text-sm">{industry}</p>
          </div>
        )}
        {(enrichment?.sources?.length ?? 0) > 0 && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Sources</p>
            <p className="text-sm">{enrichment?.sources?.length} web hits</p>
          </div>
        )}
      </div>

      {description && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <h3 className="mb-3 text-xs font-semibold uppercase text-muted-foreground">About</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </div>
      )}

      {(display?.technologies?.length || display?.competitors?.length || display?.cultureSummary) && (
        <div className="mb-6 flex flex-wrap gap-2">
          {display?.technologies?.slice(0, 8).map((tech) => (
            <span
              key={tech}
              className="rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground"
            >
              {tech}
            </span>
          ))}
          {display?.competitors?.slice(0, 4).map((c) => (
            <span
              key={c}
              className="rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground"
            >
              vs {c}
            </span>
          ))}
          {display?.cultureSummary && (
            <p className="w-full text-sm text-muted-foreground">{display.cultureSummary}</p>
          )}
        </div>
      )}

      <div className="mb-6 rounded-lg border border-border bg-card p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase text-muted-foreground">Your notes</h3>
        <textarea
          value={notes || user?.notes || ""}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Personal notes about this company…"
          className="mb-2 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={handleSaveNotes}
          disabled={updateCompany.isPending}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          {updateCompany.isPending ? "Saving…" : "Save notes"}
        </button>
      </div>

      {research && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase text-muted-foreground">
              Research dossier {user?.research ? "(yours)" : "(directory)"}
            </h3>
            {user?.lastResearchedAt && (
              <span className="text-[10px] text-muted-foreground">
                Updated {new Date(user.lastResearchedAt).toLocaleString()}
              </span>
            )}
          </div>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">
            {research}
          </pre>
        </div>
      )}

      {companyJobs.length > 0 && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <h3 className="mb-3 text-xs font-semibold uppercase text-muted-foreground">Your jobs</h3>
          <div className="space-y-2">
            {jobsPagination.pageItems.map((job) => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className="block rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/10"
              >
                <span className="font-medium text-foreground">{job.role}</span>
                <span className="ml-2 text-xs text-muted-foreground">{job.status}</span>
              </Link>
            ))}
          </div>
          <PaginationBar
            page={jobsPagination.page}
            pageSize={jobsPagination.pageSize}
            totalItems={companyJobs.length}
            totalPages={jobsPagination.totalPages}
            showingFrom={jobsPagination.showingFrom}
            showingTo={jobsPagination.showingTo}
            onPageChange={jobsPagination.setPage}
            onPageSizeChange={jobsPagination.setPageSize}
          />
        </div>
      )}

      <div className="mb-6 rounded-lg border border-border bg-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Contacts</h3>
          <button
            type="button"
            onClick={() => setShowContactForm((v) => !v)}
            className="text-xs font-medium text-brand hover:underline"
          >
            {showContactForm ? "Cancel" : "+ Add contact"}
          </button>
        </div>
        {showContactForm && slug && (
          <div className="mb-4">
            <ContactForm
              companySlug={slug}
              onCreated={() => setShowContactForm(false)}
            />
          </div>
        )}
        <ContactList
          contacts={(company.contacts ?? []).map((c) => ({
            id: c.id,
            name: c.name,
            email: c.email,
            linkedinUrl: c.linkedinUrl,
            role: c.role,
            title: c.title,
          }))}
          emptyMessage="No contacts at this company yet."
        />
      </div>
    </div>
  );
}
